import React from 'react';
import { graphql } from 'gatsby';
import { useI18next } from 'gatsby-plugin-react-i18next';
import Layout, { ListTLink } from '../components/Layout';
import SEO from '../components/seo';
import MagneticButton from '../components/MagneticButton';
import Icon from '../components/Icon';
import GiveContentTrans from '../components/GiveContentTrans';
import FramerTemplateRequestForm from '../components/FramerTemplateRequestForm';
import FramerTemplatesWebGLBackground from '../components/FramerTemplatesWebGLBackground';
import { templateTranslationsBySlug } from '../data/framerTemplates';
import { localizeTemplate } from '../data/framerTemplates/localize';
import { getTemplateUiCopy } from '../data/framerTemplates/ui';
import { templateMeta as aevaTemplate } from './framer-templates/ai-startup-website-template';
import { templateMeta as clauseTemplate } from './framer-templates/law-firm-website-template';
import { templateMeta as deckkerTemplate } from './framer-templates/saas-website-template';
import { templateMeta as dentraTemplate } from './framer-templates/dentra-dental-clinic-framer-template';
import { templateMeta as dentryTemplate } from './framer-templates/dental-clinic-website-template';
import { templateMeta as fiidbakkTemplate } from './framer-templates/fiidbakk';
import { templateMeta as lithosTemplate } from './framer-templates/architecture-website-template';
import { templateMeta as mldTemplate } from './framer-templates/mold-inspection-website-template';
import { templateMeta as moroTemplate } from './framer-templates/venture-capital-website-template';

const baseTemplates = [
  fiidbakkTemplate,
  dentraTemplate,
  dentryTemplate,
  clauseTemplate,
  aevaTemplate,
  moroTemplate,
  deckkerTemplate,
  lithosTemplate,
  mldTemplate,
].sort((a, b) => a.order - b.order);

function FaqToggle({ label }) {
  return (
    <>
      <button type="button" className="faq-trigger" aria-expanded="false">
        <span className="visually-hidden">{label}</span>
      </button>
      <div className="faq-arrow faq-arrow--span-icon" aria-hidden="true">
        <span className="faq-arrow__line faq-arrow__line--horizontal" />
        <span className="faq-arrow__line faq-arrow__line--vertical" />
      </div>
    </>
  );
}

function getListingStructuredData({ templates, language, listingPath, name, description }) {
  const isEnglish = language === 'en';

  return [
    {
      '@type': 'WebPage',
      name,
      description,
      url: `https://give.ee${listingPath}`,
      inLanguage: language,
    },
    {
      '@type': 'ItemList',
      name,
      itemListElement: templates.map((template, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: template.name,
        url: template.url
          ? `https://give.ee${
              isEnglish && !template.url.startsWith('/en/') ? '/en' : ''
            }${template.url}`
          : template.marketplaceUrl || `https://give.ee${listingPath}`,
      })),
    },
  ];
}

function TemplateCard({ template, ui }) {
  const localUrl = template.url;
  const thumbnail = template.thumbnailAlt
    ? React.cloneElement(template.thumbnail, { alt: template.thumbnailAlt })
    : template.thumbnail;
  const thumbnailHover =
    template.thumbnailHover && template.thumbnailHoverAlt
      ? React.cloneElement(template.thumbnailHover, { alt: template.thumbnailHoverAlt })
      : template.thumbnailHover;
  const categoryLabel = template.category.replace(/ website template$/i, ' Framer template');
  const defaultTags = [
    template.price && template.price !== template.status
      ? `${template.status}, ${template.price}`
      : template.status,
    template.pageCount ? `${template.pageCount} ${ui.pageCount}` : null,
    template.cmsPages?.length ? 'CMS' : null,
    ...(template.tags || []).slice(0, 4),
  ].filter(Boolean);
  const tags = template.cardTags || defaultTags;
  const isPremium = template.status?.toLowerCase() === 'premium';

  const handleCardPointerMove = (event) => {
    const card = event.currentTarget.closest('.template-card');
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const left = x;
    const top = y;
    const right = rect.width - x;
    const bottom = rect.height - y;
    const edgeDistance = Math.min(left, top, right, bottom);
    const edgeRange = Math.min(rect.width, rect.height) * 0.52;
    const edgeIntensity = Math.max(0, Math.min(1, 1 - edgeDistance / edgeRange));

    card.style.setProperty('--cardMouseX', `${x}px`);
    card.style.setProperty('--cardMouseY', `${y}px`);
    card.style.setProperty('--cardBorderGlow', (0.18 + edgeIntensity * 0.82).toFixed(3));
  };

  const handleCardPointerLeave = (event) => {
    const card = event.currentTarget.closest('.template-card');
    if (!card) return;

    card.style.removeProperty('--cardMouseX');
    card.style.removeProperty('--cardMouseY');
    card.style.removeProperty('--cardBorderGlow');
  };

  const content = (
    <div
      className="template-card-inner"
      onMouseMove={handleCardPointerMove}
      onMouseLeave={handleCardPointerLeave}
    >
      <div className="template-card-media">
        <div className="template-card-image template-card-image-primary">{thumbnail}</div>
        {thumbnailHover && (
          <div className="template-card-image template-card-image-hover">{thumbnailHover}</div>
        )}
      </div>
      <div className="template-card-link">
        <div className="icon icon16">
          <Icon name="arrowtopright" />
        </div>
      </div>
      <div className="template-card-copy">
        <p className={`template-card-name${isPremium ? ' is-premium' : ''}`}>
          {template.statusLabel || template.status}
        </p>
        <h2>{template.cardTitle || categoryLabel}</h2>
        <div className="template-card-tags">
          {tags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <ListTLink to={localUrl} class="template-card template-card--linked">
      {content}
    </ListTLink>
  );
}

function FramerTemplatesPage() {
  const { language } = useI18next();
  const ui = getTemplateUiCopy(language);
  const isEnglish = String(language || '').startsWith('en');
  const listingPath = isEnglish ? '/en/framer-templates/' : '/valmis-kodulehed/';
  const templates = baseTemplates.map((template) => {
    const localizedTemplate = localizeTemplate(
      template,
      templateTranslationsBySlug[template.slug],
      language
    );

    return {
      ...localizedTemplate,
      url: isEnglish
        ? template.url
        : localizedTemplate.detailAvailable
          ? localizedTemplate.canonicalPath
          : `/en${template.url}`,
      statusLabel: template.status?.toLowerCase() === 'premium' ? ui.premium : ui.free,
    };
  });

  return (
    <Layout>
      <main className="open-service-page framer-service framer-templates-page framer-templates-page--webgl outer-grid">
        <FramerTemplatesWebGLBackground />
        <GiveContentTrans language="et">
          <SEO
            title="Valmis kodulehed ettevõtetele"
            description="Vali professionaalselt kujundatud valmis koduleht, kohanda sisu ise ja avalda see oma domeenil. Konto, sisu ja ligipääsud jäävad sulle."
            image="/pagecover-en-default.jpg"
            imageAlt="Give’i valmis kodulehtede valmislahendused"
            canonical="/valmis-kodulehed/"
            alternates={[
              { lang: 'et', href: '/valmis-kodulehed/' },
              { lang: 'en', href: '/en/framer-templates/' },
            ]}
            xDefault="et"
            structuredData={getListingStructuredData({
              templates,
              language: 'et',
              listingPath: '/valmis-kodulehed/',
              name: 'Give’i valmis kodulehed',
              description:
                'Vali tasuta või tasuline Framer kodulehe põhi, kohanda sisu ise ja avalda see oma domeenil. Frameri ligipääs jääb sulle.',
            })}
          />
        </GiveContentTrans>
        <GiveContentTrans language="en">
          <SEO
            title="Framer Website Templates by Give | Free and Premium Templates"
            description="Browse free and premium Framer website templates by Give for dental clinics, law firms, SaaS companies, AI startups, architecture studios, investment firms, and local service businesses."
            image="/pagecover-en-default.jpg"
            imageAlt="Framer templates by Give"
            canonical="/en/framer-templates/"
            alternates={[
              { lang: 'et', href: '/valmis-kodulehed/' },
              { lang: 'en', href: '/en/framer-templates/' },
            ]}
            xDefault="et"
            structuredData={getListingStructuredData({
              templates,
              language: 'en',
              listingPath: '/en/framer-templates/',
              name: 'Framer templates by Give',
              description:
                'Browse free and premium Framer website templates by Give for dental clinics, law firms, SaaS companies, AI startups, architecture studios, investment firms, and local service businesses.',
            })}
          />
        </GiveContentTrans>

        <section className="hero template-hero">
          <div className="service-tag-container">
            <GiveContentTrans language="et">
              <span className="service-tag">Framer templiidid</span>
            </GiveContentTrans>
            <GiveContentTrans language="en">
              <span className="service-tag">Framer templates by Give</span>
            </GiveContentTrans>
          </div>
          <GiveContentTrans language="et">
            <h1>Valmis kodulehed, mida saad ise hallata</h1>
          </GiveContentTrans>
          <GiveContentTrans language="en">
            <h1>Free and premium Framer website templates</h1>
          </GiveContentTrans>
          <div className="page-copy">
            <GiveContentTrans language="et">
              <p>
                Vali professionaalselt kujundatud Framer kodulehe põhi ja pane see ise üles. Frameri
                konto, domeen, sisu ja ligipääsud jäävad sulle.
              </p>
            </GiveContentTrans>
            <GiveContentTrans language="en">
              <p>
                Launch a sharper website without starting from scratch. Each template combines
                polished design, practical business structure, CMS and SEO basics, giving you a
                strong foundation you can adapt and publish yourself.
              </p>
            </GiveContentTrans>
            <div className="button-group">
              <a href="#templates" className="heading4">
                <GiveContentTrans language="et">
                  <MagneticButton
                    addionalClasses="cta cta-xl"
                    buttonLabel="Vaata valmis kodulehti"
                    magneticDistance="150"
                    magneticSpeed="0.4"
                  />
                </GiveContentTrans>
                <GiveContentTrans language="en">
                  <MagneticButton
                    addionalClasses="cta cta-xl"
                    buttonLabel="Browse templates"
                    magneticDistance="150"
                    magneticSpeed="0.4"
                  />
                </GiveContentTrans>
              </a>
            </div>
          </div>
        </section>

        <section className="description-section inner-grid template-intro">
          <div className="columns-3">
            <div className="column">
              <div className="icon icon64 icon-bg">
                <Icon name="diamond" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h2>Kvaliteetne kujundus</h2>
                  <p>
                    Iga lahendus ühendab läbimõeldud visuaali, selge struktuuri ja hea tehnilise SEO
                    põhja.
                  </p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h2>Listed on Framer Marketplace</h2>
                  <p>
                    Selected templates have passed Framer Marketplace review for quality standards.
                  </p>
                </>
              </GiveContentTrans>
            </div>
            <div className="column">
              <div className="icon icon64 icon-bg">
                <Icon name="ranking" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h2>Lihtne ise hallata</h2>
                  <p>
                    Frameris on ülilihtne muuta tekste, pilte, hallata CMS-i sisu visuaalselt ilma
                    arendaja abita.
                  </p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h2>Designed with 20 years of experience</h2>
                  <p>
                    Built around the structure, content and details real business websites need.
                  </p>
                </>
              </GiveContentTrans>
            </div>
            <div className="column">
              <div className="icon icon64 icon-bg">
                <Icon name="lighting" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h2>Ei jää agentuurist sõltuvaks</h2>
                  <p>
                    Koduleht töötab sinu Frameri kontol, mitte Give’i kontrolli all. Domeen ja
                    ligipääsud kuuluvad sulle.
                  </p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h2>Easy to edit and make your own</h2>
                  <p>Change copy, images, CMS content and branding without rebuilding the site.</p>
                </>
              </GiveContentTrans>
            </div>
          </div>
        </section>

        <section className="inner-grid template-grid-section" id="templates">
          <div className="content-col content-col-half page-copy">
            <GiveContentTrans language="et">
              <>
                <h2>Vali kodulehe põhi ja tee see enda omaks</h2>
                <p>
                  Frameri tarkvara ja AI-agendi ning meie kujundatud läbimõeldud kodulehe põhjaga
                  saad suure osa tööst ise ära teha, ilma et peaksid alustama tühjalt lehelt.
                </p>
              </>
            </GiveContentTrans>
            <GiveContentTrans language="en">
              <>
                <h2>Start with a website that’s already 80% there</h2>
                <p>
                  Change the copy, images, pages, CMS content and calls to action, then publish when
                  it feels like yours.
                </p>
              </>
            </GiveContentTrans>
          </div>
          <div className="templates-grid">
            {templates.map((template) => (
              <TemplateCard key={template.name} template={template} ui={ui} />
            ))}
          </div>
        </section>

        <section className="service-full-section framer-sites template-process">
          <div className="content-row full-row">
            <div className="content-col content-col-half page-copy">
              <GiveContentTrans language="et">
                <>
                  <h2>Valmis koduleht või erilahendus?</h2>
                  <p>
                    Valmis kodulehe põhi sobib isetegijale ja alustavale ettevõttele kui resursid on
                    piiratud. Eritellimus sobib siis, kui vajad oma brändi, sisu ja ärieesmärkide
                    järgi loodud veebilehte.
                  </p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h2>Template or custom Framer website?</h2>
                  <p>
                    We build custom websites for a living, so of course we prefer custom. But not
                    every business needs that investment. Our templates are for those who still want
                    a good website without the custom price tag.
                  </p>
                </>
              </GiveContentTrans>
            </div>
          </div>
          <div className="for-who-container">
            <div className="content-col-half page-copy">
              <GiveContentTrans language="et">
                <>
                  <h3>Pane koduleht ise üles</h3>
                  <p>
                    Vali sobiv kujundus, kopeeri see enda Frameri kontole, lisa tekstid ja pildid
                    ning ühenda oma domeen.
                  </p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h3>Use a Framer template</h3>
                  <p>
                    Best when you already know what you need, want a lower-cost starting point, and
                    feel comfortable editing the site yourself.
                  </p>
                </>
              </GiveContentTrans>
              <a href="#templates" className="heading4 template-process-button">
                <GiveContentTrans language="et">
                  <MagneticButton
                    addionalClasses="cta"
                    buttonLabel="Vaata valmis kodulehti"
                    magneticDistance="150"
                    magneticSpeed="0.4"
                  />
                </GiveContentTrans>
                <GiveContentTrans language="en">
                  <MagneticButton
                    addionalClasses="cta"
                    buttonLabel="Browse templates"
                    magneticDistance="150"
                    magneticSpeed="0.4"
                  />
                </GiveContentTrans>
              </a>
            </div>
            <div className="content-col-half page-copy">
              <GiveContentTrans language="et">
                <>
                  <h3>Telli eritellimusel Frameri veebileht</h3>
                  <p>
                    Vali erilahendus, kui vajad originaalset disaini, selgemat sõnumit,
                    SEO-struktuuri ja sinu ettevõtte järgi ehitatud müügiteekonda.
                  </p>
                  <ListTLink
                    to="/teenused/veebilehe-valmistamine-frameriga/"
                    class="heading4 template-process-button"
                  >
                    <MagneticButton
                      addionalClasses="cta"
                      buttonLabel="Telli Frameri veebileht"
                      magneticDistance="150"
                      magneticSpeed="0.4"
                    />
                  </ListTLink>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h3>Order a custom Framer website</h3>
                  <p>
                    Pick this if your website needs original design, sharper messaging, SEO
                    structure, and a sales path built around your brand.
                  </p>
                  <ListTLink
                    to="/services/framer-websites/"
                    class="heading4 template-process-button"
                  >
                    <MagneticButton
                      addionalClasses="cta"
                      buttonLabel="Go custom"
                      magneticDistance="150"
                      magneticSpeed="0.4"
                    />
                  </ListTLink>
                </>
              </GiveContentTrans>
            </div>
          </div>
        </section>

        <section className="description-section inner-grid template-why-section">
          <div className="content-col content-col-full page-copy">
            <GiveContentTrans language="et">
              <>
                <h2>Valmis koduleht, mille üle säilib sul kontroll</h2>
                <p>
                  Halda sisu ise, kasuta oma domeeni ja hoia veebileht enda Frameri kontol. Ei
                  mingit agentuurilõksu, kus sulle müüakse odav pakett, et hiljem lisateenustega
                  asju peale suruda.
                </p>
              </>
            </GiveContentTrans>
            <GiveContentTrans language="en">
              <>
                <h2>Why use Framer website templates?</h2>
                <p>
                  Framer gives you a fast, secure, and editable website foundation without the usual
                  maintenance burden of traditional website platforms.
                </p>
              </>
            </GiveContentTrans>
          </div>
          <div className="template-reasons-grid">
            <article className="template-info-card page-copy">
              <div className="icon icon64 icon-bg">
                <Icon name="diamond" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h3>Turvaline ja muretu</h3>
                  <p>Pluginaid ega WordPressi versiooni pole vaja uuendada.</p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h3>Safe and secure</h3>
                  <p>No plugin stack, theme conflicts or constant update cycle.</p>
                </>
              </GiveContentTrans>
            </article>
            <article className="template-info-card page-copy">
              <div className="icon icon64 icon-bg">
                <Icon name="lighting" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h3>Kiire hosting</h3>
                  <p>Framer pakub kiiret ja turvalist ülemaailmset veebimajutust.</p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h3>Fast hosting built in</h3>
                  <p>Modern global hosting keeps pages fast across devices and locations.</p>
                </>
              </GiveContentTrans>
            </article>
            <article className="template-info-card page-copy">
              <div className="icon icon64 icon-bg">
                <Icon name="ranking" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h3>SEO-sõbralik</h3>
                  <p>
                    Halda ise pealkirju, kirjeldusi, ümbersuunamisi, indekseerimist, saidikaarte ja
                    CMS-i sisu.
                  </p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h3>SEO-friendly</h3>
                  <p>Manage titles, descriptions, redirects, indexing, sitemaps and CMS content.</p>
                </>
              </GiveContentTrans>
            </article>
            <article className="template-info-card page-copy">
              <div className="icon icon64 icon-bg">
                <Icon name="sandclock" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h3>Muuda ilma arendajata</h3>
                  <p>
                    Sa ei pea iga muudatuse jaoks oma veebiagentuuri järel ootama. Kodulehe sisu
                    uuendamine on ülilihtne.
                  </p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h3>Edit without a developer</h3>
                  <p>Update copy, images, pages and CMS content visually yourself.</p>
                </>
              </GiveContentTrans>
            </article>
            <article className="template-info-card page-copy">
              <div className="icon icon64 icon-bg">
                <Icon name="diamond" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h3>Väiksem stardikulu</h3>
                  <p>
                    Alusta viimistletud kujundusest ilma eritellimusel veebilehe investeeringuta.
                  </p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h3>Lower cost to get started</h3>
                  <p>
                    Launch with a polished design and invest in custom work when it makes sense.
                  </p>
                </>
              </GiveContentTrans>
            </article>
            <article className="template-info-card page-copy">
              <div className="icon icon64 icon-bg">
                <Icon name="lighting" />
              </div>
              <GiveContentTrans language="et">
                <>
                  <h3>Hoolikalt disainitud</h3>
                  <p>Iga valmis koduleht on loodud konkreetse tegevusala vajadusi arvestades.</p>
                </>
              </GiveContentTrans>
              <GiveContentTrans language="en">
                <>
                  <h3>Everything in one place</h3>
                  <p>Design, responsive layouts, CMS, forms and publishing in one platform.</p>
                </>
              </GiveContentTrans>
            </article>
          </div>
        </section>

        <section className="faq-section inner-grid template-faq-section">
          <div className="section-title">
            <GiveContentTrans language="et">
              <>
                <p>Korduma kippuvad küsimused</p>
                <h2>Frameri valmis kodulehtede kohta</h2>
              </>
            </GiveContentTrans>
            <GiveContentTrans language="en">
              <>
                <p>Frequently asked questions</p>
                <h2>About Framer templates</h2>
              </>
            </GiveContentTrans>
          </div>

          <div className="content-row has-media">
            <div id="faq">
              <GiveContentTrans language="et">
                <ul>
                  <li>
                    <FaqToggle label="Ava või sulge vastus" />
                    <h3>Kas valmis kodulehed on tasuta või tasulised?</h3>
                    <div className="answer">
                      <p>
                        Osa kodulehe valmislahendusi on tasuta ja osa tasulised. Kui soovid
                        kodulehte kasutada oma domeenil siis pead maksma Framerile paketi tasu.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Ava või sulge vastus" />
                    <h3>Kas saan kodulehe ise täielikult valmis teha?</h3>
                    <div className="answer">
                      <p>
                        Jah. Saad valitud lahenduse oma Frameri kontole kopeerida, selle ise sisuga
                        täita, domeeni ühendada ja avaldada. See on lihtne. Frameril on ka AI agent
                        kes saab sind aidata.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Ava või sulge vastus" />
                    <h3>Kas Give seadistab või hooldab valmis kodulehti?</h3>
                    <div className="answer">
                      <p>
                        Ei. Valmis kodulehed on mõeldud iseseisvaks kasutamiseks. Kui vajad Give’i
                        abi, loome sulle{' '}
                        <ListTLink to="/teenused/veebilehe-valmistamine-frameriga/">
                          eritellimusel Frameri veebilehe
                        </ListTLink>
                        , mis lähtub sinu brändist, sisust ja ärieesmärkidest.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Ava või sulge vastus" />
                    <h3>Kellele valmis koduleht sobib?</h3>
                    <div className="answer">
                      <p>
                        Valmis koduleht sobib väikeettevõttele, kes soovib kiiresti veebilehe
                        avaldada ning on valmis olemasolevat kujundust oma sisu ja visuaalidega
                        kohandama.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Ava või sulge vastus" />
                    <h3>Kellele kuuluvad konto, domeen ja veebilehe sisu?</h3>
                    <div className="answer">
                      <p>
                        Meie ei taha sinult mitte midagi. Pakume lihtsalt tasuta või ühekordse
                        väikese tasuga premium kodulehe põhjasid. Veebileht töötab sinu Frameri
                        kontol, domeen on sinu nimel ning säilitad ligipääsu kogu sisule ja
                        seadetele.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Ava või sulge vastus" />
                    <h3>Kas teie valmis kodulehed on hea SEO-ga?</h3>
                    <div className="answer">
                      <p>
                        Oleme kujundanud need läbimõeldud struktuuriga. Framer annab hea tehnilise
                        SEO põhja ning meie lahendused on üles ehitatud läbimõeldud struktuuriga.
                        Otsingupositsioon sõltub siiski ka sisust, märksõnastrateegiast, sise- ja
                        välislinkidest, autoriteedist ning konkurentsist.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Ava või sulge vastus" />
                    <h3>Miks Framer on ettevõtte veebilehe jaoks parem kui WordPress?</h3>
                    <div className="answer">
                      <p>
                        Paljude tänapäevaste turunduslehtede, maandumislehtede, idufirmade veebide,
                        portfooliote ja teenuseveebide puhul on Framer WordPressist lihtsam, kiirem
                        üles seada ning väiksema hooldusvajadusega. WordPress võib endiselt sobida
                        keerukaks sisuhalduseks, paljudest pluginatest sõltuvatele lahendustele või
                        kohandatud serverifunktsioonidele.
                      </p>
                    </div>
                  </li>
                </ul>
              </GiveContentTrans>

              <GiveContentTrans language="en">
                <ul>
                  <li>
                    <FaqToggle label="Toggle FAQ answer" />
                    <h3>Are these Framer templates free or paid?</h3>
                    <div className="answer">
                      <p>
                        Some templates are free and some may be paid. Each template card shows
                        whether it is free or premium before you open it.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Toggle FAQ answer" />
                    <h3>Can I use these templates without hiring Give?</h3>
                    <div className="answer">
                      <p>
                        Yes. The templates are made for DIY use in Framer. You can duplicate or buy
                        the template, edit it yourself, and launch it without hiring us.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Toggle FAQ answer" />
                    <h3>Does Give customize individual templates?</h3>
                    <div className="answer">
                      <p>
                        Usually, no. Give focuses on{' '}
                        <ListTLink to="/services/framer-websites/">
                          custom Framer websites
                        </ListTLink>{' '}
                        rather than small template-editing tasks. If you need deeper strategy,
                        original design, SEO structure, and a more serious business website, a
                        custom project is the better fit.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Toggle FAQ answer" />
                    <h3>When should I use a Framer template?</h3>
                    <div className="answer">
                      <p>
                        Use a template when you want a faster, lower-cost starting point and you are
                        comfortable adapting the design, copy, images, pages, and CMS content
                        yourself.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Toggle FAQ answer" />
                    <h3>When should I order a custom Framer website?</h3>
                    <div className="answer">
                      <p>
                        Order a custom website when your positioning, brand, content, SEO strategy,
                        conversion path, or visual direction needs expert thinking from the start.
                        Or if you just want to save your own time.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Toggle FAQ answer" />
                    <h3>Are Framer templates good for SEO?</h3>
                    <div className="answer">
                      <p>
                        They can be a good foundation. Framer gives you control over titles, meta
                        descriptions, headings, redirects, Open Graph images, sitemap settings,
                        indexing, and CMS content. But website ranking still depends on your
                        content, keyword strategy, internal and external links, authority, and
                        competition.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FaqToggle label="Toggle FAQ answer" />
                    <h3>Is Framer better than WordPress for business websites?</h3>
                    <div className="answer">
                      <p>
                        For many modern marketing websites, landing pages, startup sites,
                        portfolios, and service websites, Framer can be simpler, faster, and easier
                        to maintain than WordPress. WordPress can still make sense for complex
                        publishing, plugin-heavy systems, or custom backend functionality.
                      </p>
                    </div>
                  </li>
                </ul>
              </GiveContentTrans>
            </div>
          </div>
        </section>

        <section
          className="description-section cta-section-framer inner-grid template-final-cta"
          id="template-request"
        >
          <div className="page-copy cta-block">
            <GiveContentTrans language="et">
              <>
                <FramerTemplateRequestForm
                  title="Ei leidnud sobivat valmis kodulehte?"
                  description="Kirjelda, millist kodulehte vajad. Kui loome sellise valmislahenduse, anname sulle sellest esimesena teada."
                  labels={{
                    requestLabel: 'Millise valmis kodulehe peaksime järgmisena looma?',
                    requestPlaceholder:
                      'Näiteks koduleht luksushotellile, raamatupidamisbüroole, hambaarstile või AI-ettevõttele...',
                    next: 'Edasi',
                    backLabel: 'Tagasi mallisoovi juurde',
                    emailHeading: 'Jäta oma e-post, et saaksime ühendust hoida.',
                    emailLabel: 'Sinu e-post',
                    emailPlaceholder: 'Sinu e-post',
                    send: 'Saada',
                    consent:
                      'Nõustun saama Give’ilt e-kirju uute valmislahenduste, veebidisaini, SEO ja digiturunduse kohta. Ei mingit sisutut uudiskirja. Võin igal ajal loobuda.',
                    success: 'Aitäh! Kui loome sellise valmis kodulehe, anname sulle teada.',
                  }}
                />
                <div className="template-final-order">
                  <span className="template-final-separator-label">või</span>
                  <ListTLink
                    to="/teenused/veebilehe-valmistamine-frameriga/"
                    class="heading4 template-final-primary"
                  >
                    <MagneticButton
                      addionalClasses="cta cta-xl template-final-button"
                      buttonLabel="Telli Frameri veebileht"
                      magneticDistance="150"
                      magneticSpeed="0.4"
                    />
                  </ListTLink>
                </div>
              </>
            </GiveContentTrans>

            <GiveContentTrans language="en">
              <>
                <FramerTemplateRequestForm
                  title="Didn’t find the right Framer template?"
                  description="Tell us what kind of website you need. If we build a template for it, you’ll be the first to know."
                  labels={{
                    requestLabel: 'What template should we build next?',
                    requestPlaceholder:
                      'Example: Framer template for a luxury hotel, accounting firm, dentist, AI SaaS...',
                    next: 'Next',
                    backLabel: 'Back to template request',
                    emailHeading: 'Leave your e-mail so we stay connected.',
                    emailLabel: 'Your email',
                    emailPlaceholder: 'Your email',
                    send: 'Send',
                    consent:
                      'I agree to receive emails from Give about new Framer templates, web design, SEO, and digital marketing. No generic newsletter filler. I can unsubscribe at any time.',
                    success: 'Thanks. If we build a template like this, we’ll let you know.',
                  }}
                />
                <div className="template-final-order">
                  <span className="template-final-separator-label">or</span>
                  <ListTLink
                    to="/services/framer-websites/"
                    class="heading4 template-final-primary"
                  >
                    <MagneticButton
                      addionalClasses="cta cta-xl template-final-button"
                      buttonLabel="Order Framer website"
                      magneticDistance="150"
                      magneticSpeed="0.4"
                    />
                  </ListTLink>
                </div>
              </>
            </GiveContentTrans>
          </div>
        </section>
      </main>
    </Layout>
  );
}

export default FramerTemplatesPage;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
